import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { fitLabels, renderPage, type PageSpec, type Product, type Template } from '@/entities/label';
import { ExportButtons, type ExportTarget } from '@/features/label-export';
import { Field, Fold, Page } from '@/shared/ui';
import { editorReducer, initialEditor, layoutVisible } from '../model/editor';
import { ProductForm } from './ProductForm';

const TEMPLATES: { value: Template; label: string; short: string }[] = [
  { value: 'classic', label: 'Classic — header / body / footer', short: 'Classic' },
  { value: 'price', label: 'Price-first — large price focus', short: 'Price-first' },
  { value: 'minimal', label: 'Minimal — clean, no header bar', short: 'Minimal' },
  { value: 'retail', label: 'Retail — full stacked layout', short: 'Retail' },
];

// A label maker: the page title and the download buttons in the top bar, the form on the left (folding groups), and the live
// preview of the printable page on the right. Used by both the 4×6 sticker page and the letter sheet page; `spec` says which.
export function LabelEditor({ spec }: { spec: PageSpec }) {
  const [state, dispatch] = useReducer(editorReducer(spec), undefined, initialEditor);
  const { storeName, orient, layout, template, tab, products } = state;
  const opts = { orient, layout, template };
  const count = spec.count(layout);

  // the latest products, for the lookup (which finishes after the person may have typed more)
  const latest = useRef(products);
  useEffect(() => { latest.current = products; }, [products]);
  const readProduct = useCallback((idx: number) => latest.current[idx]!, []);
  const fill = useCallback((idx: number, patch: Partial<Product>) => dispatch({ type: 'field', idx, patch }), []);

  // ---- the preview: redrawn 150 ms after the last change (80 ms after a window resize), one redraw at a time ----
  const page = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const scaler = useRef<HTMLDivElement>(null);
  const scratch = useRef<HTMLDivElement>(null);
  const [meta, setMeta] = useState('');
  const [previewTitle, setPreviewTitle] = useState(spec.previewTitle(opts));
  const drawArgs = useRef({ opts, products, storeName });
  useEffect(() => { drawArgs.current = { opts: { orient, layout, template }, products, storeName }; });
  const busy = useRef(false);
  const pending = useRef(false);

  const drawOnce = useCallback(async () => {
    const { opts: o, products: ps, storeName: store } = drawArgs.current;
    const lp = page.current, st = stage.current, wr = wrapper.current, sc = scaler.current, qr = scratch.current;
    if (!lp || !st || !wr || !sc || !qr) return;
    const { html, n, dims } = await renderPage(spec, o, ps, store, qr);
    const { pageW, pageH } = dims;
    lp.style.width = pageW + 'px';
    lp.style.height = pageH + 'px';
    lp.innerHTML = html;
    fitLabels(lp);
    // scale the page to fit the stage (never larger than real size)
    const sw = st.clientWidth - 48;
    const sh = st.clientHeight - 48;
    const scale = Math.min(sw / pageW, sh / pageH, 1);
    wr.style.width = Math.round(pageW * scale) + 'px';
    wr.style.height = Math.round(pageH * scale) + 'px';
    sc.style.transform = `scale(${scale})`;
    setPreviewTitle(spec.previewTitle(o));
    setMeta(spec.meta(n, dims, o));
  }, [spec]);

  // one redraw at a time; a change that arrives during a redraw is drawn right after it
  const draw = useCallback(async () => {
    if (busy.current) { pending.current = true; return; }
    busy.current = true;
    try {
      do { pending.current = false; await drawOnce(); } while (pending.current);
    } finally {
      busy.current = false;
    }
  }, [drawOnce]);

  useEffect(() => { const t = setTimeout(draw, 150); return () => clearTimeout(t); }, [draw, storeName, orient, layout, template, products]);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => { clearTimeout(t); t = setTimeout(draw, 80); };
    window.addEventListener('resize', onResize);
    // the brand fonts change text widths, so the labels are fitted again once they have loaded
    document.fonts?.ready.then(() => draw());
    return () => { window.removeEventListener('resize', onResize); clearTimeout(t); };
  }, [draw]);

  const exportTarget = (): ExportTarget => ({
    page: () => page.current?.firstElementChild as HTMLElement | null,
    landscape: spec.landscape(opts),
    pdf: spec.pdfFormat(opts),
    printSize: spec.printSize(opts),
    fileName: spec.fileName,
  });

  const layoutLabel = spec.layouts.find((l) => l.key === layout)?.label ?? layout;
  const setupNote = [spec.hasOrientation ? (orient === 'landscape' ? '6 × 4' : '4 × 6') : null, `${layoutLabel} per ${spec.hasOrientation ? 'sticker' : 'page'}`, TEMPLATES.find((t) => t.value === template)?.short].filter(Boolean).join(' · ');

  const tabs = (
    <div className="card-tabs" role="group" aria-label="Labels">
      {count > 1 && Array.from({ length: count }, (_, i) => (
        <button key={i} type="button" aria-pressed={i === tab} className={i === tab ? 'active' : ''} onClick={() => dispatch({ type: 'tab', value: i })}>Label {i + 1}</button>
      ))}
    </div>
  );

  return (
    <Page title={spec.subtitle} actions={<ExportButtons target={exportTarget} pdfNote={spec.kind === 'sticker' ? '4 × 6 page' : 'US Letter page'} />}>
      <div className="editor">
        <div className="card side-panel">
          <Fold title="Print Setup" note={setupNote} open remember="labels.setup">
            <Field label="Store / Brand Name">
              {(id) => <input id={id} type="text" placeholder="e.g. ACME STORE" value={storeName} onChange={(e) => dispatch({ type: 'store', value: e.target.value })} />}
            </Field>

            {spec.hasOrientation && <>
              <span className="choice-label" id="orientLabel">Sticker Orientation</span>
              <div className="choices two" role="group" aria-labelledby="orientLabel">
                <button type="button" className="choice" aria-pressed={orient === 'portrait'} onClick={() => dispatch({ type: 'orient', value: 'portrait' })}>
                  <span className="gc" style={{ width: 16, height: 24 }} aria-hidden="true" />4 &times; 6
                </button>
                <button type="button" className="choice" aria-pressed={orient === 'landscape'} onClick={() => dispatch({ type: 'orient', value: 'landscape' })}>
                  <span className="gc" style={{ width: 24, height: 16 }} aria-hidden="true" />6 &times; 4
                </button>
              </div>
            </>}

            <span className="choice-label" id="layoutLabel">{spec.layoutTitle}</span>
            <div className={`choices${spec.hasOrientation ? ' two' : ''}`} role="group" aria-labelledby="layoutLabel">
              {spec.layouts.map((l) => layoutVisible(spec, state, l.key) && (
                <button key={l.key} type="button" className="choice" aria-pressed={layout === l.key} onClick={() => dispatch({ type: 'layout', value: l.key })}>
                  <span className="grid-viz" style={{ gridTemplateColumns: l.cols === 2 ? '1fr 1fr' : '1fr' }} aria-hidden="true">
                    {l.cells.map((c, i) => <span key={i} className="gc" style={{ width: c.w, height: c.h }} />)}
                  </span>
                  {l.label}
                </button>
              ))}
            </div>

            <Field label="Template Style" className="field" style={{ marginBottom: 0 }}>
              {(id) => (
                <select id={id} value={template} onChange={(e) => dispatch({ type: 'template', value: e.target.value as Template })}>
                  {TEMPLATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              )}
            </Field>
          </Fold>

          <div key={`${orient}|${layout}`}>
            <ProductForm tab={tab} count={count} product={products[tab]!} limits={{ name: spec.nameMax, desc: spec.descMax }}
              set={(patch) => dispatch({ type: 'field', idx: tab, patch })} readProduct={readProduct} fill={fill}
              copyFirst={() => dispatch({ type: 'copyFirst' })} tabs={tabs} />
          </div>
        </div>

        <section className="card editor-preview" aria-label="Preview">
          <div className="preview-head">
            <h3>{previewTitle}</h3>
            <span className="preview-meta">{meta}</span>
          </div>
          <div className="preview-stage" ref={stage}>
            <div id="previewWrapper" ref={wrapper}>
              <div id="previewScaler" ref={scaler}>
                <div id="letterPage" className="label-page" ref={page} />
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* off-screen box the QR library draws into */}
      <div id="qrScratch" ref={scratch} aria-hidden="true" />
    </Page>
  );
}
